// The pane of the caster select. It tells one caster in short: its symbol, its
// name, and its scroll slots, then one line about it, then its four slots as
// four even cells, and under them the words of one skill. Its button picks the
// caster or puts it back, and has a check box. Garrett, 2026-10-13: "add a
// checkbox to the lock button in the flow to help make it more obvious". On a
// touch screen that button is the only way to pick, because a tap on a caster
// fills this pane. See `CasterSelect.ts`.
// Changed 2026-10-14. Each skill was a line with its vessel, its name, and its
// blurb, and on a phone the pane scrolled. Garrett: "I think we should just
// have scroll icons and summarize each caster, rather than showing their
// entire descriptions", "Show empty slots too as little empty squares", and "I
// dont want it to scroll". A skill's words show under a pointer or a tap. The
// line about the caster is Rob's, in `content/casters.ts`. With none, the pane
// shows no line.
// In the wide select the pane shows the four slots as four cards in a grid of
// two by two, each with its vessel, the name of the spell, and its blurb, in
// place of the cells. Added 2026-10-14. Garrett: "on desktop we can just have
// four pills here for the spells with icon/name/description instead of the
// click tabs (which still work for mobile)", then "do 2x2 scroll slots, same
// height and width". The pane draws both, and `caster-select.css` shows one.

import type { Caster } from '../content/casters';
import { openingSlots, type Slot } from '../game/casters';
import { el } from '../lib/dom';
import { spellById } from '../spells/library';
import { pickVerb } from '../state/layout';
import { createCasterPawn } from './CasterMark';
import { createCheckBox } from './CheckBox';
import { createKeyIcon } from './keyIcons';
import { createSlotFace } from './SlotFace';

/** Plain words, not final copy. */
const PICK_LABEL = 'Pick';
const PICKED_LABEL = 'Picked';
const EMPTY_SLOT_LABEL = 'Empty scroll slot';
const scrollSlotNote = (count: number): string => `${count} scroll slot${count === 1 ? '' : 's'}`;
const readPrompt = (): string => `${pickVerb()} a skill to read it.`;

export type CasterPaneView = {
  caster: Caster;
  /** The caster is one of the picks. */
  picked: boolean;
  /** Every pick is made, so no other caster can be picked. */
  full: boolean;
  /** Null in a match, where the pane is only read: it has no pick button. */
  onToggle: (() => void) | null;
};

export type CasterPane = {
  root: HTMLElement;
  draw: (view: CasterPaneView) => void;
};

/**
 * The vessel of a skill as a full spell of a match: paid, with the liquid in
 * the color of the caster, the glint, and the sparks. Each spell has its own
 * pattern and time, from its id, so no two vessels move together. It has no
 * click and no tooltip: the line under the cells has its words. Changed
 * 2026-10-14. It was paper with an ink icon. Garrett: "color the spells in the
 * chooser like they appear as full spells in the game, sparkles and all".
 */
const skillVessel = (slot: Slot): HTMLElement => {
  const face = createSlotFace({
    slot,
    energy: slot.kind === 'skill' ? slot.cost : 0,
    open: true,
    onCast: null,
  });
  delete face.dataset.tip;
  delete face.dataset.tipTitle;
  delete face.dataset.tipSpell;
  delete face.dataset.tipNote;
  return el('span', { className: 'caster caster--pick caster-select__vessel' }, [
    el('ol', { className: 'caster__slots' }, [face]),
  ]);
};

export const createCasterPane = (): CasterPane => {
  const root = el('div', { className: 'caster-select__pane' });
  /** The caster the pane tells, and the place of the skill whose words show. -1 shows the prompt. */
  let drawnCaster: string | null = null;
  let shown = -1;
  let last: CasterPaneView | null = null;

  const show = (place: number): void => {
    shown = place;
    if (last) draw(last);
  };

  const cell = (slot: Slot, place: number): HTMLElement => {
    if (slot.spellId === null) {
      return el(
        'span',
        {
          className: 'caster-select__cell caster-select__cell--empty',
          ariaLabel: EMPTY_SLOT_LABEL,
        },
        [createKeyIcon('scroll')],
      );
    }
    const spell = spellById(slot.spellId);
    return el(
      'button',
      {
        className: 'caster-select__cell',
        type: 'button',
        ariaLabel: spell.name,
        ariaPressed: String(place === shown),
        // A pointer shows the words as it passes. A tap shows them, and a second tap hides them.
        onpointerenter: (event: PointerEvent) => {
          if (event.pointerType === 'mouse' && shown !== place) show(place);
        },
        onclick: () => show(shown === place ? -1 : place),
      },
      [skillVessel(slot)],
    );
  };

  /** A card of the wide grid: the vessel, the name, and the blurb, or an empty scroll slot. */
  const skillRow = (slot: Slot): HTMLElement => {
    if (slot.spellId === null) {
      return el('li', { className: 'caster-select__skill caster-select__skill--empty' }, [
        el('span', { className: 'caster-select__cell caster-select__cell--empty' }, [
          createKeyIcon('scroll'),
        ]),
        el('span', { className: 'caster-select__skill-name', textContent: EMPTY_SLOT_LABEL }),
      ]);
    }
    const spell = spellById(slot.spellId);
    return el('li', { className: 'caster-select__skill' }, [
      el('span', { className: 'caster-select__cell' }, [skillVessel(slot)]),
      el('span', { className: 'caster-select__skill-name', textContent: spell.name }),
      el('span', { className: 'caster-select__skill-blurb', textContent: spell.blurb }),
    ]);
  };

  const detail = (slots: readonly Slot[]): HTMLElement => {
    const spellId = slots[shown]?.spellId;
    if (!spellId) return el('p', { className: 'caster-select__detail', textContent: readPrompt() });
    const spell = spellById(spellId);
    return el('p', { className: 'caster-select__detail' }, [
      el('b', { textContent: `${spell.name}. ` }),
      spell.blurb,
    ]);
  };

  const draw = (view: CasterPaneView): void => {
    const { caster, picked, full, onToggle } = view;
    last = view;
    if (drawnCaster !== caster.id) {
      drawnCaster = caster.id;
      shown = -1;
    }
    const slots = openingSlots(caster);
    root.dataset.caster = caster.id;
    root.replaceChildren(
      el('div', { className: 'caster-select__pane-head' }, [
        createCasterPawn(caster.id),
        el('span', { className: 'caster-select__who' }, [
          el('span', { className: 'caster-select__name', textContent: caster.name }),
          el('span', {
            className: 'caster-select__note',
            textContent: scrollSlotNote(caster.scrollSlots),
          }),
        ]),
        // The box says the button is a switch: empty, then with a tick. A press on a picked caster puts it back.
        ...(onToggle
          ? [
              el(
                'button',
                {
                  className: 'caster-select__pick',
                  type: 'button',
                  disabled: !picked && full,
                  ariaPressed: String(picked),
                  onclick: onToggle,
                },
                [createCheckBox(picked), picked ? PICKED_LABEL : PICK_LABEL],
              ),
            ]
          : []),
      ]),
      ...(caster.summary
        ? [el('p', { className: 'caster-select__summary', textContent: caster.summary })]
        : []),
      el('div', { className: 'caster-select__cells' }, slots.map(cell)),
      detail(slots),
      // The wide select shows this list in place of the cells and the line under them.
      el('ol', { className: 'caster-select__skills' }, slots.map(skillRow)),
    );
  };

  return { root, draw };
};
